import React, { useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Info, Pencil, Plus, Trash2, Trophy } from "lucide-react";
import { AdminTopbar } from "../../layouts/AdminLayout";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Button, EmptyState, Loading, Modal, SectionCard } from "../../components/ui";
import { apiDelete, apiPost, apiPut, useApi } from "../../api/client";
import { Competition, MatchInfo, NamedAggregate } from "../../data/types";

/*
 * Named aggregates — the trophies.
 *
 * A meeting's prizes are not simply "each day" and "the whole shoot". They are
 * named trophies over whichever matches the programme says, and the same match
 * routinely counts towards more than one:
 *
 *   John Trophy  = Match 1 + Match 2 + Match 3
 *   Scott Trophy = Match 1 + Match 3 + Match 5
 *
 * So this page is where an administrator writes that programme down: a name and
 * the matches that count. Nothing here assumes a match belongs to one trophy —
 * ticking Match 1 in two trophies is normal, not a mistake.
 *
 * The grand aggregate stays automatic and is not listed here; every meeting has
 * one and nobody should have to create it.
 */
export default function AdminAggregates() {
  const { data: competitions, loading: loadingComps } = useApi<Competition[]>("/competitions", []);
  const { data: matches } = useApi<MatchInfo[]>("/matches", []);
  const [competitionId, setCompetitionId] = useState("");
  const [editing, setEditing] = useState<NamedAggregate | null>(null);
  const [pending, setPending] = useState<NamedAggregate | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "ok" | "bad"; text: string } | null>(null);

  // Land on a competition rather than an empty page.
  useEffect(() => {
    if (!competitionId && competitions.length) setCompetitionId(competitions[0].id);
  }, [competitions, competitionId]);

  const { data: aggregates, loading, reload } = useApi<NamedAggregate[]>(
    competitionId ? `/competitions/${competitionId}/named-aggregates` : null,
    []
  );

  // Listed in the order they are shot, so ticking "Match 1, 2, 3" is reading down.
  const compMatches = useMemo(
    () =>
      matches
        .filter((m) => m.competitionId === competitionId)
        .sort((a, b) => (a.n || 0) - (b.n || 0) || a.name.localeCompare(b.name)),
    [matches, competitionId]
  );
  const matchName = (id: string) => compMatches.find((m) => m.id === id)?.name || id;

  const save = async (draft: NamedAggregate) => {
    setBusy(true);
    try {
      const body = { competitionId, name: draft.name, matchIds: draft.matchIds, n: draft.n };
      const r = draft.id
        ? await apiPut<{ message: string }>(`/admin/aggregates/${encodeURIComponent(draft.id)}`, body)
        : await apiPost<{ message: string }>("/admin/aggregates", body);
      setEditing(null);
      reload();
      setNotice({ tone: "ok", text: r.message || "Saved." });
    } catch (e) {
      setNotice({ tone: "bad", text: (e as Error).message });
    }
    setBusy(false);
  };

  const remove = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await apiDelete(`/admin/aggregates/${encodeURIComponent(pending.id)}`);
      setNotice({ tone: "ok", text: `"${pending.name}" removed.` });
      reload();
    } catch (e) {
      setNotice({ tone: "bad", text: (e as Error).message });
    }
    setPending(null);
    setBusy(false);
  };

  return (
    <div>
      <AdminTopbar
        title="Custom Aggregates"
        subtitle="Trophies over a set of matches of their own — the ones CID and the shoot dates cannot express."
        actions={
          <Button
            disabled={!competitionId || !compMatches.length}
            onClick={() => setEditing({ id: "", competitionId, name: "", matchIds: [] })}
          >
            <Plus size={16} /> Add Aggregate
          </Button>
        }
      />

      {notice && (
        <div
          className={`mb-3 flex items-start gap-2.5 rounded-xl border px-4 py-3 text-[13px] ${
            notice.tone === "ok" ? "border-success/30 bg-success-soft text-success" : "border-danger/30 bg-danger-soft text-danger"
          }`}
        >
          {notice.tone === "ok" ? <CheckCircle2 size={16} className="mt-px shrink-0" /> : <AlertCircle size={16} className="mt-px shrink-0" />}
          <span className="min-w-0 font-semibold">{notice.text}</span>
          <button className="ml-auto shrink-0 font-bold opacity-70 hover:opacity-100" onClick={() => setNotice(null)}>✕</button>
        </div>
      )}

      <div className="card mb-4 flex flex-wrap items-center gap-3 p-4">
        <label className="text-[13px] font-bold text-ink">Event</label>
        <select
          value={competitionId}
          onChange={(e) => setCompetitionId(e.target.value)}
          className="h-10 min-w-[260px] rounded-lg border border-line bg-white px-3 text-sm font-semibold text-ink"
        >
          {!competitions.length && <option value="">No competitions yet</option>}
          {competitions.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <span className="text-[13px] text-muted">
          {compMatches.length} {compMatches.length === 1 ? "match" : "matches"} available
        </span>
      </div>

      <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-primary/20 bg-primary-soft px-4 py-3 text-[13px] text-primary">
        <Info size={16} className="mt-px shrink-0" />
        <span>
          <strong>Championship, Day and Grand aggregates are produced automatically</strong> — championships from the CID
          column on the workbook's Match sheet, days from the match dates. They are not listed here and need no setup.
          <br />
          This page is for the trophies that cut across those, where a match counts towards more than one prize — a Long
          Range Aggregate over the 800, 900 and 1000 yard matches that already count towards the Kings, or the John and
          Scott Trophies that share Match 1.
        </span>
      </div>

      <SectionCard title="Named Aggregates">
        {loadingComps || loading ? (
          <Loading />
        ) : !aggregates.length ? (
          <EmptyState
            icon={Trophy}
            title="No custom aggregates"
            message="Championship, Day and Grand aggregates already appear on the results without being set up here. Add one only for a trophy that counts a set of matches of its own."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-line bg-slate-50">
                  <th className="th">Aggregate</th>
                  <th className="th">Matches Counted</th>
                  <th className="th text-center">Stages</th>
                  <th className="th text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {aggregates.map((a) => (
                  <tr key={a.id} className="transition-colors hover:bg-slate-50">
                    <td className="td">
                      <span className="flex items-center gap-2.5 font-bold text-ink">
                        <Trophy size={16} className="text-warning" /> {a.name}
                      </span>
                    </td>
                    <td className="td">
                      <span className="flex flex-wrap gap-1">
                        {a.matchIds.map((id) => (
                          <span key={id} className="rounded-md bg-primary-soft px-2 py-0.5 text-[12px] font-semibold text-primary">
                            {matchName(id)}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className="td text-center font-bold">{a.matchIds.length}</td>
                    <td className="td text-right">
                      <span className="inline-flex gap-1.5">
                        <button
                          onClick={() => setEditing(a)}
                          className="rounded-lg p-2 text-muted transition-colors hover:bg-slate-100 hover:text-primary"
                          title="Edit"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={() => setPending(a)}
                          className="rounded-lg p-2 text-muted transition-colors hover:bg-danger-soft hover:text-danger"
                          title="Remove"
                        >
                          <Trash2 size={15} />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {editing && (
        <AggregateEditor
          draft={editing}
          matches={compMatches}
          busy={busy}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}

      <ConfirmDialog
        open={!!pending}
        title="Remove this aggregate?"
        intro={<>This aggregate will no longer appear in the results list.</>}
        items={pending ? [pending.name] : []}
        note="Only the definition is removed — the matches and their results are untouched, and the aggregate can be created again at any time."
        confirmLabel="Remove Aggregate"
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={remove}
      />
    </div>
  );
}

function AggregateEditor({
  draft,
  matches,
  busy,
  onCancel,
  onSave,
}: {
  draft: NamedAggregate;
  matches: MatchInfo[];
  busy: boolean;
  onCancel: () => void;
  onSave: (d: NamedAggregate) => void;
}) {
  const [name, setName] = useState(draft.name);
  const [matchIds, setMatchIds] = useState<string[]>(draft.matchIds);

  const toggle = (id: string) =>
    setMatchIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const valid = name.trim().length > 0 && matchIds.length > 0;

  return (
    <Modal
      open
      wide
      title={draft.id ? `Edit ${draft.name}` : "Add Aggregate"}
      onClose={onCancel}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button disabled={!valid || busy} onClick={() => onSave({ ...draft, name: name.trim(), matchIds })}>
            {busy ? "Saving..." : "Save Aggregate"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1.5 block text-[13px] font-bold text-ink">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Trophy"
            className="h-10 w-full rounded-lg border border-line px-3 text-sm text-ink"
          />
          <p className="mt-1 text-[12px] text-muted">The name competitors will see in the results list.</p>
        </div>

        <div>
          <label className="mb-1.5 block text-[13px] font-bold text-ink">
            Matches that count ({matchIds.length} selected)
          </label>
          {!matches.length ? (
            <p className="rounded-lg border border-line px-3 py-6 text-center text-[13px] text-muted">
              This competition has no matches yet.
            </p>
          ) : (
            <div className="max-h-[320px] space-y-1 overflow-y-auto rounded-lg border border-line p-2">
              {matches.map((m) => (
                <label
                  key={m.id}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                    matchIds.includes(m.id) ? "bg-primary-soft" : "hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={matchIds.includes(m.id)}
                    onChange={() => toggle(m.id)}
                    className="h-4 w-4 accent-primary"
                  />
                  <span className="font-bold text-ink">{m.name}</span>
                  <span className="text-[12px] text-muted">
                    {[m.date, m.distance, m.shots ? `${m.shots} shots` : ""].filter(Boolean).join(" · ")}
                  </span>
                </label>
              ))}
            </div>
          )}
          <p className="mt-1 text-[12px] text-muted">
            Scores are summed across the matches ticked here, then placed within each class and grade.
          </p>
        </div>
      </div>
    </Modal>
  );
}
